import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Paper, TextField, MenuItem, Button, CircularProgress, Alert } from "@mui/material";
import { Download, UploadCloud, FileText } from "lucide-react";
import { buildDsrTemplateCsv, downloadCsv } from "./dsrCsvTemplate";
import "./DailySituationReport.css";

const backendUrl = (process.env.REACT_APP_BACKEND_API_URL_LOCAL || "").replace(/\/$/, "");
const MUNICIPALITIES_URL = `${backendUrl}/Municipalities/municipalities_list/`;
const SUB_MUNICIPALITIES_URL = `${backendUrl}/Municipalities/sub_municipalities_list/`;
const SUCO_LIST_URL = `${backendUrl}/Municipalities/suco_list/`;
const BULK_UPLOAD_URL = `${backendUrl}/common/acp-bulk-upload/`;

const authHeaders = () => {
    const token = localStorage.getItem("access_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Native <input type="date"> already yields YYYY-MM-DD - same format the
// save/list endpoints expect, so no reformatting is needed at download time.
const todayISO = () => new Date().toISOString().slice(0, 10);

// A native date input only opens its calendar when you hit the small icon -
// this opens it on a click anywhere in the box instead. showPicker() needs
// a real browser (Chrome/Edge/recent Firefox); older/unsupported browsers
// just fall back to the input's normal click behavior.
const openDatePicker = (e) => {
    if (e.target.showPicker) {
        try { e.target.showPicker(); } catch { /* no-op: not focused/supported */ }
    }
};

// Bulk-upload for the Situation Report sheet. Download a CSV pre-filled
// with every suco (or just one municipality/post), fill it in, upload it
// back - the import upserts into statistics_acp per (date, suco_code).
const DsrBulkTemplate = () => {
    const [date, setDate] = useState(todayISO());
    const [municipalities, setMunicipalities] = useState([]);
    const [selectedMunicipality, setSelectedMunicipality] = useState("");
    const [subMunicipalities, setSubMunicipalities] = useState([]);
    const [selectedSubMunicipality, setSelectedSubMunicipality] = useState("");
    const [allSucos, setAllSucos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadMessage, setUploadMessage] = useState("");
    const [uploadSeverity, setUploadSeverity] = useState("info");
    const [uploading, setUploading] = useState(false);
    const [isDragActive, setIsDragActive] = useState(false);

    useEffect(() => {
        fetch(MUNICIPALITIES_URL, { method: "POST", headers: authHeaders() })
            .then((res) => res.json())
            .then((data) => setMunicipalities(Array.isArray(data) ? data : []))
            .catch((err) => console.error("Error fetching municipalities:", err));

        // All sucos up front - used as the default template contents when
        // no municipality is picked, so "Download Sample" always yields a
        // full sheet.
        fetch(SUCO_LIST_URL, { method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() }, body: "{}" })
            .then((res) => res.json())
            .then((data) => setAllSucos(Array.isArray(data) ? data : []))
            .catch((err) => console.error("Error fetching all sucos:", err));
    }, []);

    useEffect(() => {
        setSelectedSubMunicipality("");
        if (!selectedMunicipality) { setSubMunicipalities([]); return; }
        fetch(SUB_MUNICIPALITIES_URL, {
            method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({ adm2_code: selectedMunicipality }), // endpoint's own naming quirk - filters by adm1_code
        })
            .then((res) => res.json())
            .then((data) => setSubMunicipalities(Array.isArray(data) ? data : []))
            .catch((err) => { console.error("Error fetching sub-municipalities:", err); setSubMunicipalities([]); });
    }, [selectedMunicipality]);

    const fetchSucos = async () => {
        if (selectedSubMunicipality) {
            const res = await fetch(SUCO_LIST_URL, {
                method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ adm2_code: selectedSubMunicipality }),
            });
            if (!res.ok) throw new Error("Failed to load sucos for this administrative post.");
            return res.json();
        }
        const subRes = await fetch(SUB_MUNICIPALITIES_URL, {
            method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({ adm2_code: selectedMunicipality }),
        });
        const subs = await subRes.json();
        if (!subRes.ok) throw new Error("Failed to load administrative posts for this municipality.");
        const lists = await Promise.all((subs || []).map(async (sub) => {
            const res = await fetch(SUCO_LIST_URL, {
                method: "POST", headers: { "Content-Type": "application/json", ...authHeaders() },
                body: JSON.stringify({ adm2_code: sub.adm2_code }),
            });
            const data = await res.json();
            return res.ok ? data : [];
        }));
        return lists.flat();
    };

    const handleDownload = async () => {
        if (!selectedMunicipality) {
            // No area picked - hand back a sheet with every suco.
            downloadCsv(
                `situation_report_upload_template_all_sucos_${date}.csv`,
                buildDsrTemplateCsv(allSucos.length ? allSucos : null, date),
            );
            return;
        }
        setLoading(true);
        setError("");
        try {
            const sucoRows = await fetchSucos();
            const municipalityName = municipalities.find((m) => m.adm1_code === selectedMunicipality)?.adm1 || selectedMunicipality;
            const subName = selectedSubMunicipality
                ? `_${subMunicipalities.find((s) => s.adm2_code === selectedSubMunicipality)?.adm2 || selectedSubMunicipality}`
                : "";
            downloadCsv(`daily_situation_report_${municipalityName}${subName}_${date}_upload_template.csv`, buildDsrTemplateCsv(sucoRows, date));
        } catch (err) {
            console.error("Error building DSR bulk template:", err);
            setError(err.message || "Failed to load sucos for this area.");
        } finally {
            setLoading(false);
        }
    };

    const handleChooseFile = () => fileInputRef.current?.click();

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files?.[0] || null);
        setUploadMessage("");
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        setUploadMessage("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragActive(false);
        const file = e.dataTransfer.files?.[0];
        if (file) {
            setSelectedFile(file);
            setUploadMessage("");
        }
    };

    const handleUploadClick = async () => {
        if (!selectedFile) return;
        setUploading(true);
        setUploadMessage("");
        try {
            const formData = new FormData();
            formData.append("file", selectedFile);
            const res = await fetch(BULK_UPLOAD_URL, {
                method: "POST",
                headers: authHeaders(),
                body: formData,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || "Upload failed.");
            const parts = [`Imported ${data.saved} row(s)`];
            if (data.dates?.length) parts.push(`for ${data.dates.join(", ")}`);
            setUploadSeverity((data.errors?.length ? "warning" : "success"));
            setUploadMessage(
                parts.join(" ") + (data.errors?.length ? ` — ${data.errors.length} row(s) skipped: ${data.errors.slice(0, 3).join("; ")}` : "")
            );
            // Clear the picked file so the dropzone is ready for the next
            // upload - also stops clicking Upload again from resubmitting
            // the same file (statistics_acp is append-only, so that would
            // duplicate every row in it).
            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (err) {
            console.error("Bulk upload failed:", err);
            setUploadSeverity("error");
            setUploadMessage(err.message || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box className="dsr-container">
            <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
                {/* Upload - the main event, dropzone-style */}
                <Paper elevation={0} sx={{ flex: "1 1 420px", p: 4, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                    <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "18px", mb: 0.5 }}>
                        Upload Data
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "13.5px", mb: 2.5 }}>
                        Fill in the downloaded sample and bring it back here. Each row is matched on its
                        Suco Code + Date, so re-uploading a corrected file overwrites, never duplicates.
                    </Typography>

                    <Box
                        onClick={handleChooseFile}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                            border: `2px dashed ${isDragActive ? "#2563eb" : "#cbd5e1"}`,
                            borderRadius: "14px",
                            backgroundColor: isDragActive ? "#eff6ff" : "#f8fafc",
                            p: 5,
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "background-color 0.15s ease, border-color 0.15s ease",
                            "&:hover": { borderColor: "#94a3b8", backgroundColor: "#f1f5f9" },
                        }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv"
                            style={{ display: "none" }}
                            onChange={handleFileChange}
                        />
                        <UploadCloud size={36} color={isDragActive ? "#2563eb" : "#94a3b8"} style={{ marginBottom: 8 }} />
                        {selectedFile ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                                <FileText size={16} color="#2563eb" />
                                <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "14px" }}>
                                    {selectedFile.name}
                                </Typography>
                                <Button
                                    size="small" onClick={(e) => { e.stopPropagation(); handleClearFile(); }}
                                    sx={{ minWidth: "auto", color: "#94a3b8", textTransform: "none", fontSize: "12px" }}
                                >
                                    Remove
                                </Button>
                            </Box>
                        ) : (
                            <>
                                <Typography sx={{ color: "#334155", fontWeight: 600, fontSize: "14px" }}>
                                    Drag & drop your CSV here
                                </Typography>
                                <Typography sx={{ color: "#94a3b8", fontSize: "12.5px", mt: 0.5 }}>
                                    or click to browse
                                </Typography>
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <Button
                            variant="text" disabled={!selectedFile || uploading} onClick={handleUploadClick}
                            startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadCloud size={16} />}
                            sx={{
                                backgroundColor: "#2563eb18", color: "#2563eb", border: "1px solid #2563eb30",
                                textTransform: "none", fontWeight: 700, px: 4,
                                "&:hover": { backgroundColor: "#2563eb28" },
                                "&.Mui-disabled": { backgroundColor: "#f1f5f9", color: "#94a3b8", border: "1px solid #e2e8f0" },
                            }}
                        >
                            {uploading ? "Uploading..." : "Upload"}
                        </Button>
                    </Box>
                    {uploadMessage && <Alert severity={uploadSeverity} sx={{ mt: 2.5, fontSize: "13px" }}>{uploadMessage}</Alert>}
                </Paper>

                {/* Template download - secondary, kept in the right corner */}
                <Paper elevation={0} sx={{ width: 300, flexShrink: 0, p: 3, borderRadius: "16px", border: "1px solid #e2e8f0" }}>
                    <Typography sx={{ color: "#1e293b", fontWeight: 600, fontSize: "15px", mb: 0.5 }}>
                        Upload Template
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "12.5px", mb: 2 }}>
                        Leave Municipality blank for a sheet with every suco, or pick one to narrow it.
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                        <TextField
                            label="Date" type="date" size="small" value={date}
                            onChange={(e) => setDate(e.target.value)}
                            onClick={openDatePicker}
                            inputProps={{ onClick: openDatePicker }}
                            InputLabelProps={{ shrink: true }} fullWidth
                        />
                        <TextField
                            label="Municipality (optional)" select size="small" value={selectedMunicipality}
                            onChange={(e) => setSelectedMunicipality(e.target.value)} fullWidth
                        >
                            <MenuItem value=""><em style={{ color: "#94a3b8" }}>None — blank template</em></MenuItem>
                            {municipalities.map((m) => <MenuItem key={m.adm1_code} value={m.adm1_code}>{m.adm1}</MenuItem>)}
                        </TextField>
                        <TextField
                            label="Sub-Municipality (optional)" select size="small" value={selectedSubMunicipality}
                            onChange={(e) => setSelectedSubMunicipality(e.target.value)}
                            disabled={!selectedMunicipality} fullWidth
                        >
                            <MenuItem value=""><em style={{ color: "#94a3b8" }}>{selectedMunicipality ? "All administrative posts" : "Select a municipality first"}</em></MenuItem>
                            {subMunicipalities.map((s) => <MenuItem key={s.adm2_code} value={s.adm2_code}>{s.adm2}</MenuItem>)}
                        </TextField>
                        <Button
                            variant="text" disabled={loading} onClick={handleDownload} fullWidth
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Download size={16} />}
                            sx={{
                                backgroundColor: "#2563eb18", color: "#2563eb", border: "1px solid #2563eb30",
                                textTransform: "none", fontWeight: 700,
                                "&:hover": { backgroundColor: "#2563eb28" },
                            }}
                        >
                            {loading ? "Loading sucos..." : "Download Sample"}
                        </Button>
                    </Box>
                    {error && <Alert severity="error" sx={{ mt: 2, fontSize: "12.5px" }}>{error}</Alert>}
                </Paper>
            </Box>
        </Box>
    );
};

export default DsrBulkTemplate;
